import { cn, currencyFormatter } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/shad/ui/alert'
import { Users } from 'lucide-react'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import FormButtons from '../_components/form-buttons'
import { StudentAwardInformation } from '../award-form'
import { getStudentAwardBlockerNote } from '../award-form-utils'
import { StudentCard } from './_components/student-card'

interface SelectStudentsStepFormProps {
  selectedStudents: StudentAwardInformation[]
  purse: Purse
  onNextClick: () => void
  onBackClick: () => void
}

export function SelectStudentsStepForm({
  selectedStudents,
  purse,
  onNextClick,
  onBackClick,
}: SelectStudentsStepFormProps) {
  const totalLeftInPurse = purse.balance_available

  const totalToBeDistributed = selectedStudents.reduce((acc, student) => {
    return student.amount_to_be_awarded + acc
  }, 0)

  // A student the purse cannot fund — unassociated, or with no amount from ServiceNow — has to be
  // removed from the batch before it can be submitted, so awarding stays blocked while any remain.
  const blockedStudents = selectedStudents.filter((student) =>
    Boolean(
      getStudentAwardBlockerNote({
        isAssociatedWithPurse: student.is_associated_with_purse,
        hasAwardAmount: student.has_award_amount,
      })
    )
  )

  function hasEnoughToCoverAllStudents() {
    return totalToBeDistributed <= totalLeftInPurse
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Selected Students
            </h2>
            <p className="text-sm text-gray-600">
              {selectedStudents.length} student
              {selectedStudents.length !== 1 ? 's' : ''} will receive
              scholarships
            </p>
          </div>
        </div>

        <div className="">
          <div className="overflow-y-auto">
            {selectedStudents.map((student) => {
              return <StudentCard key={student.id} student={student} />
            })}
          </div>

          <div className=" py-6 border-t border-gray-200 rounded-b-xl">
            <div className="text-sm space-y-1">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Number of Students to be Awarded
                </span>
                <span className="font-medium text-gray-800">
                  {selectedStudents.length}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Total Award Amount
                </span>
                <span className="font-medium text-gray-800">
                  {currencyFormatter.format(totalToBeDistributed)}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Purse Balance
                </span>
                <span className={cn('font-medium text-gray-800')}>
                  {currencyFormatter.format(totalLeftInPurse)}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Remaining in Purse after awards
                </span>
                <span
                  className={cn(
                    'font-medium text-gray-800',
                    !hasEnoughToCoverAllStudents() && 'text-danger'
                  )}
                >
                  {currencyFormatter.format(
                    totalLeftInPurse - totalToBeDistributed
                  )}
                </span>
              </div>
            </div>
          </div>
          {blockedStudents.length > 0 && (
            <div className=" py-2">
              <Alert variant={'destructive'}>
                <AlertTitle className="text-sm">
                  {blockedStudents.length} student
                  {blockedStudents.length !== 1 ? 's' : ''} cannot be funded
                  from this purse
                </AlertTitle>
                <AlertDescription>
                  Go back and pick a different purse, or restart with a
                  selection this purse can fund.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {!hasEnoughToCoverAllStudents() && (
            <div className=" py-2">
              <Alert variant={'destructive'}>
                <AlertTitle className="text-sm">Not enough funds</AlertTitle>
                <AlertDescription>
                  The total award amount is greater than the remaining purse
                  balance.
                </AlertDescription>
              </Alert>
            </div>
          )}
          <FormButtons>
            <FormButtons.BackButton onClick={onBackClick} />
            <FormButtons.NextButton
              disabled={
                !hasEnoughToCoverAllStudents() || blockedStudents.length > 0
              }
              onClick={onNextClick}
            />
          </FormButtons>
        </div>
      </div>
    </div>
  )
}
